import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Trash, Warning, MagnifyingGlass } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const DeleteByRange = ({ onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [types, setTypes] = useState({ sales: true, purchases: true, expenses: true });
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const selectedTypes = Object.entries(types).filter(([, v]) => v).map(([k]) => k);

  const handlePreview = async () => {
    if (!startDate || !endDate) { toast.error('Select both start and end dates'); return; }
    if (selectedTypes.length === 0) { toast.error('Select at least one data type'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/data/count-by-range`, {
        start_date: startDate, end_date: endDate, types: selectedTypes,
      }, { withCredentials: true });
      setCounts(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to count records'); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/data/delete-by-range`, {
        start_date: startDate, end_date: endDate, types: selectedTypes,
      }, { withCredentials: true });
      toast.success(data.message);
      setConfirmOpen(false);
      setOpen(false);
      resetState();
      if (onDeleted) onDeleted();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  const resetState = () => {
    setStartDate('');
    setEndDate('');
    setTypes({ sales: true, purchases: true, expenses: true });
    setCounts(null);
  };

  const toggleType = (type) => setTypes(prev => ({ ...prev, [type]: !prev[type] }));

  const formatDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`;
  };

  return (
    <>
      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => { resetState(); setOpen(true); }} data-testid="delete-by-range-btn">
        <Trash size={16} weight="regular" className="mr-2" />
        Delete by Date Range
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) resetState(); setOpen(v); }}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="text-2xl tracking-tight">Delete by Date Range</DialogTitle></DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Start Date</Label>
                <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCounts(null); }} className="rounded-sm" data-testid="range-start-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">End Date</Label>
                <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCounts(null); }} className="rounded-sm" data-testid="range-end-date" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Data Types</Label>
              <div className="flex items-center gap-4">
                {[
                  { key: 'sales', label: 'Sales', color: 'text-primary' },
                  { key: 'purchases', label: 'Purchases', color: 'text-orange-600 dark:text-orange-400' },
                  { key: 'expenses', label: 'Expenses', color: 'text-destructive' },
                ].map(t => (
                  <label key={t.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={types[t.key]}
                      onChange={() => { toggleType(t.key); setCounts(null); }}
                      className="rounded"
                      data-testid={`range-type-${t.key}`}
                    />
                    <span className={types[t.key] ? t.color : 'text-muted-foreground'}>{t.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Button onClick={handlePreview} disabled={loading || !startDate || !endDate || selectedTypes.length === 0} variant="outline" className="w-full rounded-sm" data-testid="preview-count-btn">
              <MagnifyingGlass size={16} weight="bold" className="mr-2" />
              {loading ? 'Counting...' : 'Preview Records'}
            </Button>

            {counts && (
              <div className="border border-border rounded-sm p-4 space-y-2" data-testid="range-preview-counts">
                <div className="text-sm font-semibold">
                  Records from {formatDate(startDate)} to {formatDate(endDate)}:
                </div>
                <div className="space-y-1 text-sm">
                  {counts.sales !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sales</span>
                      <span className="font-semibold">{counts.sales}</span>
                    </div>
                  )}
                  {counts.purchases !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Purchases</span>
                      <span className="font-semibold">{counts.purchases}</span>
                    </div>
                  )}
                  {counts.expenses !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Expenses</span>
                      <span className="font-semibold">{counts.expenses}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-border pt-1 mt-1">
                    <span className="font-semibold">Total</span>
                    <span className="font-bold text-destructive">{counts.total}</span>
                  </div>
                </div>

                {counts.total > 0 ? (
                  <Button onClick={() => setConfirmOpen(true)} variant="destructive" className="w-full rounded-sm mt-2" data-testid="delete-range-btn">
                    <Trash size={16} weight="bold" className="mr-2" />
                    Delete {counts.total} Records
                  </Button>
                ) : (
                  <div className="text-center text-sm text-muted-foreground py-2">No records found in this range</div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="text-2xl tracking-tight">Confirm Deletion</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-sm">
              <Warning size={20} weight="fill" className="text-destructive shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-destructive">This action cannot be undone</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {counts?.total} records from {formatDate(startDate)} to {formatDate(endDate)} will be permanently deleted.
                </div>
              </div>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              {counts?.sales > 0 && <div>{counts.sales} sales records</div>}
              {counts?.purchases > 0 && <div>{counts.purchases} purchase records</div>}
              {counts?.expenses > 0 && <div>{counts.expenses} expense records</div>}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} className="flex-1 rounded-sm">Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="flex-1 rounded-sm" data-testid="confirm-delete-range">
                {deleting ? 'Deleting...' : 'Delete All'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteByRange;
